import React, { useEffect, useMemo, useState } from "react";
import {
  getDownloadURL,
  getStorage,
  listAll,
  ref,
  uploadBytes,
} from "firebase/storage";
import app_connection from "../../firebase.js";
import "./BannerCreator.css";

const BANNER_FOLDER = "banner-memes";
const MAX_SELECTION = 30;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png"];
const ACCEPTED_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png"];

function getFirebaseMessage(action, error) {
  const errorCode = error?.code ? ` (${error.code})` : "";

  return `${action} failed${errorCode}. Check Firebase Storage rules for the banner-memes folder.`;
}

function shuffleItems(items) {
  return [...items].sort(() => Math.random() - 0.5);
}

function isAcceptedImage(file) {
  const fileName = file.name.toLowerCase();

  return (
    ACCEPTED_IMAGE_TYPES.includes(file.type) &&
    ACCEPTED_IMAGE_EXTENSIONS.some((extension) => fileName.endsWith(extension))
  );
}

function isAcceptedImageName(fileName) {
  const normalizedName = fileName.toLowerCase();

  return ACCEPTED_IMAGE_EXTENSIONS.some((extension) =>
    normalizedName.endsWith(extension)
  );
}

function BannerCreator() {
  const [availableImages, setAvailableImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imageCount, setImageCount] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState("");

  const storage = useMemo(() => getStorage(app_connection), []);
  const maxSelectable = Math.min(availableImages.length, MAX_SELECTION);

  const loadImages = async () => {
    setIsLoading(true);
    setMessage("");

    try {
      const folderRef = ref(storage, BANNER_FOLDER);
      const imageRefs = await listAll(folderRef);
      const images = await Promise.all(
        imageRefs.items
          .filter((imageRef) => isAcceptedImageName(imageRef.name))
          .map(async (imageRef) => ({
            name: imageRef.name,
            path: imageRef.fullPath,
            url: await getDownloadURL(imageRef),
          }))
      );

      images.sort((a, b) => a.name.localeCompare(b.name));
      setAvailableImages(images);
      setImageCount((currentCount) =>
        Math.min(Math.max(currentCount, 1), Math.min(images.length, MAX_SELECTION) || 1)
      );
      setSelectedImages((currentImages) =>
        currentImages.filter((image) =>
          images.some((availableImage) => availableImage.path === image.path)
        )
      );
    } catch (error) {
      console.error("Banner creator image load failed", error);
      setMessage(getFirebaseMessage("Loading images", error));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadImages();
  }, []);

  const uploadImages = async (event) => {
    const uploadedFiles = Array.from(event.target.files || []);
    const files = uploadedFiles.filter(isAcceptedImage);

    if (!files.length) {
      setMessage("Choose still PNG or JPG image files to upload.");
      return;
    }

    if (files.length !== uploadedFiles.length) {
      setMessage("Some files were skipped. Use still PNG or JPG images only.");
    }

    setIsUploading(true);
    if (files.length === uploadedFiles.length) {
      setMessage("");
    }

    try {
      await Promise.all(
        files.map((file) => {
          const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
          const storagePath = `${BANNER_FOLDER}/${Date.now()}-${safeName}`;
          return uploadBytes(ref(storage, storagePath), file, {
            contentType: file.type,
          });
        })
      );

      event.target.value = "";
      setMessage(`${files.length} image${files.length === 1 ? "" : "s"} uploaded.`);
      await loadImages();
    } catch (error) {
      console.error("Banner creator upload failed", error);
      setMessage(getFirebaseMessage("Upload", error));
    } finally {
      setIsUploading(false);
    }
  };

  const randomizeImages = () => {
    if (!availableImages.length) {
      setSelectedImages([]);
      setMessage("Upload some photos first.");
      return;
    }

    setSelectedImages(shuffleItems(availableImages).slice(0, imageCount));
    setMessage("");
  };

  const toggleSelectedImage = (image) => {
    setSelectedImages((currentImages) => {
      const imageAlreadySelected = currentImages.some(
        (currentImage) => currentImage.path === image.path
      );

      if (imageAlreadySelected) {
        setMessage("");
        return currentImages.filter((currentImage) => currentImage.path !== image.path);
      }

      if (currentImages.length >= MAX_SELECTION) {
        setMessage(`You can select up to ${MAX_SELECTION} images.`);
        return currentImages;
      }

      setMessage("");
      return [...currentImages, image];
    });
  };

  const removeSelectedImage = (imagePath) => {
    setSelectedImages((currentImages) =>
      currentImages.filter((currentImage) => currentImage.path !== imagePath)
    );
    setMessage("");
  };

  const countOptions = Array.from({ length: maxSelectable }, (_, index) => index + 1);

  return (
    <main className="BannerCreator">
      <section className="BannerCreator-toolbar">
        <label className="BannerCreator-upload">
          <span>{isUploading ? "Uploading..." : "Upload photos"}</span>
          <input
            type="file"
            accept=".jpg,.jpeg,.png,image/jpeg,image/png"
            multiple
            onChange={uploadImages}
            disabled={isUploading}
          />
        </label>

        <div className="BannerCreator-controls">
          <label htmlFor="banner-image-count">Images</label>
          <select
            id="banner-image-count"
            value={imageCount}
            onChange={(event) => setImageCount(Number(event.target.value))}
            disabled={!maxSelectable}
          >
            {countOptions.length ? (
              countOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))
            ) : (
              <option value={1}>0</option>
            )}
          </select>
          <button
            type="button"
            onClick={randomizeImages}
            disabled={!availableImages.length}
          >
            Randomize
          </button>
        </div>
      </section>

      {message && <p className="BannerCreator-message">{message}</p>}

      <section className="BannerCreator-workspace">
        <aside className="BannerCreator-library" aria-label="Uploaded photos">
          <h1>Uploaded Images</h1>
          <div className="BannerCreator-scrollList">
            {isLoading && <p className="BannerCreator-empty">Loading images...</p>}
            {availableImages.map((image) => {
              const isSelected = selectedImages.some(
                (selectedImage) => selectedImage.path === image.path
              );

              return (
              <article
                className={`BannerCreator-listItem${isSelected ? " is-selected" : ""}`}
                key={image.path}
                onClick={() => toggleSelectedImage(image)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    toggleSelectedImage(image);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <img src={image.url} alt={image.name} />
                <strong>{isSelected ? "Selected" : "Select"}</strong>
                <span title={image.name}>{image.name}</span>
              </article>
              );
            })}
          </div>
        </aside>

        <section className="BannerCreator-preview" aria-label="Selected banner images">
          {selectedImages.length ? (
            <div
              className="BannerCreator-grid"
            >
              {selectedImages.map((image) => (
                <button
                  className="BannerCreator-selectedItem"
                  key={image.path}
                  type="button"
                  onClick={() => removeSelectedImage(image.path)}
                  title={`Remove ${image.name}`}
                >
                  <img src={image.url} alt={image.name} />
                </button>
              ))}
            </div>
          ) : (
            <div className="BannerCreator-placeholder">
              <h2>Selected images will appear here</h2>
              <p>Pick a count and randomize the uploaded photos.</p>
            </div>
          )}
        </section>
      </section>
    </main>
  );
}

export default BannerCreator;
